import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownTrayIcon, ClockIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import Modal from "../common/Modal";
import Loader from "../common/Loader";
import PairingHistoryTable from "./PairingHistoryTable";
import { deviceService } from "../../api/services/deviceService";
import { ReportFormat } from "../../schemas/reportSchema";

interface DeviceHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId: string;
  /** Label appended to the modal title (e.g. the device code). */
  titleContext?: string;
}

/**
 * Read-only pairing timeline for one physical device: every verification
 * code it has held and every screen it has been bound to, newest first.
 * (Reached from a device — the device-list entry point.)
 */
export default function DeviceHistoryModal({
  isOpen,
  onClose,
  deviceId,
  titleContext,
}: DeviceHistoryModalProps) {
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["deviceHistory", deviceId],
    queryFn: () => deviceService.getDeviceHistory(deviceId),
    enabled: isOpen && !!deviceId,
  });

  // The table below is capped at the first page; the export is not. Say so,
  // rather than letting the file silently disagree with what is on screen.
  const loadedCount = data?.events.length ?? 0;
  const hasUnloadedEvents = (data?.total ?? 0) > loadedCount;

  const handleDownload = async (format: ReportFormat) => {
    setIsDownloading(true);
    try {
      toast.info(`Generating ${format} history...`);

      const downloadResult = await deviceService.downloadDeviceHistory(
        deviceId,
        format,
      );

      const byteCharacters = atob(downloadResult.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadResult.mimeType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadResult.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Device history downloaded successfully");
    } catch (error) {
      console.error("Error downloading device history:", error);
      toast.error("Failed to download device history");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        titleContext ? `Device History — ${titleContext}` : "Device History"
      }
      size="full"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader size="small" />
        </div>
      ) : isError ? (
        <div className="text-center py-10 text-red-500 text-sm">
          Failed to load device history.
        </div>
      ) : !data || data.events.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          <ClockIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No history recorded for this device yet.</p>
          <p className="text-xs text-gray-400 mt-1">
            History is collected from the day the feature was enabled.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="text-xs text-gray-500">
              {hasUnloadedEvents
                ? `Showing the latest ${loadedCount} of ${data.total} events — the download includes all of them.`
                : `${data.total} event${data.total === 1 ? "" : "s"}`}
            </span>

            <div className="relative">
              <button
                type="button"
                className="rounded bg-primary-default p-2 text-white hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                title="Download Device History"
                disabled={isDownloading}
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
              {showDownloadMenu && (
                <div className="absolute right-0 z-50 mt-1 w-36 rounded-lg border border-gray-200 bg-white shadow-lg">
                  <button
                    type="button"
                    className="w-full rounded-t-lg px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setShowDownloadMenu(false);
                      handleDownload("PDF");
                    }}
                  >
                    Download PDF
                  </button>
                  <button
                    type="button"
                    className="w-full rounded-b-lg px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setShowDownloadMenu(false);
                      handleDownload("EXCEL");
                    }}
                  >
                    Download Excel
                  </button>
                </div>
              )}
            </div>
          </div>

          <PairingHistoryTable events={data.events} variant="device" />
        </>
      )}
    </Modal>
  );
}
